import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { buildConsoleRequestHeaders } from '../utils/backendRequest';

const IntegrationsContext = createContext();

function clientCoreBaseUrl() {
  return process.env.REACT_APP_CLIENT_CORE_API_URL || '';
}

function requestHeaders(token, user) {
  return buildConsoleRequestHeaders(token, user);
}

export function IntegrationsProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  const refreshInFlightRef = useRef(false);
  const accountsRef = useRef(accounts);

  accountsRef.current = accounts;

  const refreshAccounts = useCallback(async ({ environment } = {}) => {
    if (refreshInFlightRef.current) {
      return accountsRef.current;
    }

    refreshInFlightRef.current = true;
    const token = localStorage.getItem('token');
    if (!token) {
      setAccounts([]);
      setError('integrationsMissingToken');
      refreshInFlightRef.current = false;
      return [];
    }

    try {
      setLoading(true);
      setError(null);
      const url = new URL(
        `${clientCoreBaseUrl()}/v1/clients/courier_accounts`,
        window.location.origin,
      );
      if (environment) {
        url.searchParams.set('environment', environment);
      }

      const res = await fetch(url.toString(), {
        headers: requestHeaders(token, user),
      });

      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setAccounts(list);
        return list;
      }

      setAccounts([]);
      setError('integrationsLoadFailed');
      return [];
    } catch (err) {
      console.error('Failed to load courier accounts:', err);
      setAccounts([]);
      setError('integrationsLoadFailed');
      return [];
    } finally {
      setLoading(false);
      refreshInFlightRef.current = false;
    }
  }, [user]);

  const createAccount = useCallback(async (payload) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('integrationsMissingToken');
      return { ok: false };
    }

    try {
      setSaving(true);
      setError(null);
      const res = await fetch(`${clientCoreBaseUrl()}/v1/clients/courier_accounts`, {
        method: 'POST',
        headers: {
          ...requestHeaders(token, user),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload || {}),
      });

      if (res.ok) {
        return { ok: true, data: await res.json() };
      }

      setError(res.status === 409 ? 'integrationsDuplicate' : 'integrationsSaveFailed');
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Failed to create courier account:', err);
      setError('integrationsSaveFailed');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [user]);

  const updateAccount = useCallback(async (accountId, payload) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('integrationsMissingToken');
      return { ok: false };
    }

    try {
      setSaving(true);
      setError(null);
      const res = await fetch(
        `${clientCoreBaseUrl()}/v1/clients/courier_accounts/${encodeURIComponent(accountId)}`,
        {
          method: 'PUT',
          headers: {
            ...requestHeaders(token, user),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload || {}),
        },
      );

      if (res.ok) {
        return { ok: true, data: await res.json() };
      }

      setError('integrationsSaveFailed');
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Failed to update courier account:', err);
      setError('integrationsSaveFailed');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [user]);

  const listCredentials = useCallback(async (courierAccountId) => {
    const token = localStorage.getItem('token');
    if (!token || !courierAccountId) {
      return [];
    }

    try {
      const url = new URL(
        `${clientCoreBaseUrl()}/v1/clients/courier_credentials`,
        window.location.origin,
      );
      url.searchParams.set('courier_account_id', String(courierAccountId));

      const res = await fetch(url.toString(), {
        headers: requestHeaders(token, user),
      });

      if (res.ok) {
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      }
      return [];
    } catch (err) {
      console.error('Failed to load courier credentials:', err);
      return [];
    }
  }, [user]);

  const createCredentials = useCallback(async (payload) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('integrationsMissingToken');
      return { ok: false };
    }

    try {
      setError(null);
      const res = await fetch(`${clientCoreBaseUrl()}/v1/clients/courier_credentials`, {
        method: 'POST',
        headers: {
          ...requestHeaders(token, user),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload || {}),
      });

      if (res.ok) {
        return { ok: true, data: await res.json() };
      }

      setError('integrationsCredentialsSaveFailed');
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Failed to create courier credentials:', err);
      setError('integrationsCredentialsSaveFailed');
      return { ok: false };
    }
  }, [user]);

  const updateCredentials = useCallback(async (credentialsId, payload) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('integrationsMissingToken');
      return { ok: false };
    }

    try {
      setError(null);
      const res = await fetch(
        `${clientCoreBaseUrl()}/v1/clients/courier_credentials/${encodeURIComponent(credentialsId)}`,
        {
          method: 'PUT',
          headers: {
            ...requestHeaders(token, user),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload || {}),
        },
      );

      if (res.ok) {
        return { ok: true, data: await res.json() };
      }

      setError('integrationsCredentialsSaveFailed');
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Failed to update courier credentials:', err);
      setError('integrationsCredentialsSaveFailed');
      return { ok: false };
    }
  }, [user]);

  const deleteCredentials = useCallback(async (credentialsId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('integrationsMissingToken');
      return { ok: false };
    }

    try {
      setError(null);
      const res = await fetch(
        `${clientCoreBaseUrl()}/v1/clients/courier_credentials/${encodeURIComponent(credentialsId)}`,
        {
          method: 'DELETE',
          headers: requestHeaders(token, user),
        },
      );

      if (res.ok || res.status === 204) {
        return { ok: true };
      }

      setError('integrationsCredentialsDeleteFailed');
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Failed to delete courier credentials:', err);
      setError('integrationsCredentialsDeleteFailed');
      return { ok: false };
    }
  }, [user]);

  const deleteAccount = useCallback(async (accountId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('integrationsMissingToken');
      return { ok: false };
    }

    try {
      setSaving(true);
      setError(null);
      const res = await fetch(
        `${clientCoreBaseUrl()}/v1/clients/courier_accounts/${encodeURIComponent(accountId)}`,
        {
          method: 'DELETE',
          headers: requestHeaders(token, user),
        },
      );

      if (res.ok || res.status === 204) {
        return { ok: true };
      }

      setError('integrationsDeleteFailed');
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Failed to delete courier account:', err);
      setError('integrationsDeleteFailed');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  }, [user]);

  return (
    <IntegrationsContext.Provider
      value={{
        accounts,
        refreshAccounts,
        createAccount,
        updateAccount,
        deleteAccount,
        listCredentials,
        createCredentials,
        updateCredentials,
        deleteCredentials,
        loading,
        saving,
        error,
        setError,
      }}
    >
      {children}
    </IntegrationsContext.Provider>
  );
}

export function useIntegrations() {
  return useContext(IntegrationsContext);
}
